import React, { useState } from 'react';
import { MessageCircle, ChevronDown, ChevronUp, Sparkles, User } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';

/**
 * Phase 27.37.V — Kora chat transcript card.
 *
 * Renders in the lead detail page ONLY when the landing-page chatbot
 * conducted a pre-form conversation with this visitor. Data lives on
 * `lead.chat_transcript` + `lead.chat_collected_fields`, populated by
 * `POST /api/public/chat/{sid}/attach-lead` at form submission.
 *
 * Collapsed by default — agents can peek without dominating the page,
 * then expand to read the full back-and-forth.
 */
export default function LeadChatTranscriptCard({ lead }) {
  const [expanded, setExpanded] = useState(false);
  const transcript = lead?.chat_transcript || [];
  const collected = lead?.chat_collected_fields || {};
  const lang = lead?.chat_language;

  if (!transcript.length) return null;

  const messageCount = transcript.length;
  const collectedKeys = Object.keys(collected);

  return (
    <Card className="border-l-4 border-l-sky-500" data-testid="lead-chat-transcript-card">
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <MessageCircle size={16} className="text-sky-600" />
            <h3 className="text-xs font-semibold uppercase tracking-wider text-sky-700">
              Kora chat transcript
            </h3>
            <Badge variant="outline" className="text-[10px] border-sky-200 text-sky-700 bg-sky-50">
              {messageCount} {messageCount === 1 ? 'msg' : 'msgs'}
            </Badge>
            {lang && (
              <Badge variant="outline" className="text-[10px]">
                {lang === 'es' ? 'Español' : 'English'}
              </Badge>
            )}
          </div>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setExpanded((v) => !v)}
            data-testid="lead-chat-toggle-btn"
            className="text-[11px] h-7"
          >
            {expanded ? <><ChevronUp size={12} className="mr-1" /> Hide</> : <><ChevronDown size={12} className="mr-1" /> Show conversation</>}
          </Button>
        </div>

        {collectedKeys.length > 0 && (
          <div className="mb-3 rounded bg-slate-50 border border-slate-200 px-3 py-2">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">
              Kora captured
            </div>
            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
              {collectedKeys.map((k) => (
                <div key={k} className="flex justify-between gap-2">
                  <span className="text-slate-500 capitalize">{k.replace(/_/g, ' ')}:</span>
                  <span className="text-slate-800 font-medium truncate">
                    {typeof collected[k] === 'boolean' ? (collected[k] ? 'Yes' : 'No') : String(collected[k])}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {expanded && (
          <div
            className="max-h-96 overflow-y-auto space-y-2 pr-1"
            data-testid="lead-chat-transcript-messages"
          >
            {transcript.map((m, i) => {
              const isUser = m.role === 'user';
              const Icon = isUser ? User : Sparkles;
              return (
                <div key={i} className={`flex ${isUser ? 'justify-end' : 'justify-start'} gap-1.5`}>
                  {!isUser && <Icon size={12} className="text-sky-500 mt-1.5 flex-shrink-0" />}
                  <div
                    className={`max-w-[85%] rounded-2xl px-3 py-1.5 text-xs ${
                      isUser
                        ? 'bg-slate-800 text-white rounded-br-sm'
                        : 'bg-sky-50 text-slate-800 border border-sky-100 rounded-bl-sm'
                    }`}
                  >
                    {m.text}
                    {m.at && (
                      <div className={`text-[9.5px] mt-1 ${isUser ? 'text-slate-300' : 'text-slate-400'}`}>
                        {new Date(m.at).toLocaleString()}
                      </div>
                    )}
                  </div>
                  {isUser && <Icon size={12} className="text-slate-400 mt-1.5 flex-shrink-0" />}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
